import React from 'react'

import { CardItem } from '@/components/CardItem'
import { AppIcons } from '@/components/AppIcons'
import { BaseText, BaseView } from '@/components/base'
import { useTheme } from '@/hooks/useTheme'
import { WalletCurrency } from '@/domain'
import { currencyUtils } from '@/utils'
import { getDisplayCurrencyCode } from '@/utils/getDisplayCurrencyCode'
import { formatFiatCurrency } from '@/utils/currency/formatFiatCurrency'

const { currencyConditionals } = currencyUtils

interface CurrencyItemProps extends WalletCurrency {
  onPress?: (currency: WalletCurrency) => void
  descriptionIsCryptoValue?: boolean
  containerStyle?: React.CSSProperties
  contentStyle?: React.CSSProperties
  showChevronRight?: boolean
  isRelativeFiatValueInUSD?: boolean
  disabled?: boolean
}

export const CurrencyItem: React.FC<CurrencyItemProps> = ({
  onPress,
  containerStyle,
  contentStyle,
  descriptionIsCryptoValue = false,
  showChevronRight = false,
  isRelativeFiatValueInUSD = false,
  disabled = false,
  ...item
}) => {
  const theme = useTheme()
  const { isUsdt } = currencyConditionals(item.code || '')

  const isLongTextAmountAndDescriptionIsCryptoValue =
    descriptionIsCryptoValue &&
    `${item.amountWithoutNumberConversion} ${item.displayCurrencyCode}${item.revenue24hFiatAmount}`
      .length > 24
  const balanceFormatted = formatFiatCurrency({
    value: item.fiatAmount,
    showDecimals: true,
    fiatCode: item.displayCurrencyCode,
  })
  return (
    <CardItem.Root
      disabled={disabled}
      onPress={onPress ? () => onPress(item) : undefined}
      style={containerStyle}
    >
      <CardItem.CurrencyIcon
        uri={item.icon}
        bgColor={
          disabled
            ? theme.colors.background.secondaryGray
            : item.avatarBGRefColor
        }
        size={48}
        fallbackText={item.code?.charAt(0) || '?'}
      />

      <CardItem.Content style={{ gap: '8px', ...contentStyle }}>
        <BaseText fontFamily="bold" color="label.default">
          {balanceFormatted}
        </BaseText>

        <BaseView
          row={!isLongTextAmountAndDescriptionIsCryptoValue}
          align="center"
          justify="space-between"
          fullWidth={!isLongTextAmountAndDescriptionIsCryptoValue}
          gap={isLongTextAmountAndDescriptionIsCryptoValue ? 6 : 0}
        >
          <BaseText fontFamily="semibold" color="label.description">
            {descriptionIsCryptoValue
              ? `${item.amountWithoutNumberConversion} ${getDisplayCurrencyCode(item.code || '')}`
              : item.name || ''}
          </BaseText>

          {!showChevronRight && (
            <>
              {item.percentageChange24h !== 0 && !isUsdt && (
                <CardItem.FlagProfitsOrLosses
                  percentChange24h={item.percentageChange24h}
                  revenue24hFiatAmount={item.revenue24hFiatAmount}
                  fiatCode={item.fiatCurrency}
                />
              )}
            </>
          )}
        </BaseView>
      </CardItem.Content>

      {showChevronRight && (
        <CardItem.RightIcon>
          <AppIcons
            name="chevronRight"
            size={24}
            color={disabled ? 'label.disabled' : 'icon.iconCard'}
          />
        </CardItem.RightIcon>
      )}
    </CardItem.Root>
  )
}
